import { onMounted, onBeforeUnmount, ref, shallowRef } from 'vue'

const siteKey = import.meta.env.VITE_RECAPTCHA_SITEKEY as string

type V2Size = 'normal' | 'compact' | 'invisible'
type V2Theme = 'light' | 'dark'

type RenderOptions = {
  size?: V2Size
  theme?: V2Theme
  tabindex?: number
  badge?: 'bottomright' | 'bottomleft' | 'inline'
}

declare global {
  interface Window {
    grecaptcha?: any
    __grecaptchaOnLoad__?: () => void
  }
}

let scriptLoadingPromise: Promise<void> | null = null
function loadRecaptchaScript(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve()
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://www.google.com/recaptcha/api.js?onload=__grecaptchaOnLoad__&render=explicit'
    s.async = true
    s.defer = true
    window.__grecaptchaOnLoad__ = () => resolve()
    s.onerror = () => reject(new Error('Failed to load reCAPTCHA script'))
    document.head.appendChild(s)
  })

  return scriptLoadingPromise
}

export function useRecaptchaV2(options: RenderOptions) {
  const containerEl = shallowRef<HTMLElement | null>(null)
  const widgetId = ref<number | null>(null)
  const token = ref<string | null>(null)
  const ready = ref(false)
  const error = ref<string | null>(null)

  async function render() {
    await loadRecaptchaScript()
    if (!containerEl.value) return
    const params: any = {
      sitekey: siteKey,
      theme: options.theme ?? 'light',
      size: options.size ?? 'normal',
      tabindex: options.tabindex ?? 0,
      callback: (t: string) => {
        token.value = t
        error.value = null
      },
      'expired-callback': () => {
        token.value = null
        error.value = 'expired'
      },
      'error-callback': () => {
        token.value = null
        error.value = 'error'
      },
    }
    if (options.size === 'invisible' && options.badge) {
      params.badge = options.badge
    }

    widgetId.value = window.grecaptcha.render(containerEl.value, params)
    ready.value = true
  }

  function reset() {
    if (widgetId.value !== null && window.grecaptcha) {
      window.grecaptcha.reset(widgetId.value)
      token.value = null
    }
  }

  async function execute(): Promise<string> {
    await loadRecaptchaScript()
    if (widgetId.value === null) throw new Error('Widget not rendered yet')

    return new Promise<string>((resolve, reject) => {
      try {
        // clear any previous token before a new run
        token.value = null

        // kick off the challenge; when solved, 'callback' in render() sets token.value
        window.grecaptcha.execute(widgetId.value, { action: 'submit' })

        // wait until the render callback gives a token
        const startedAt = Date.now()
        const MAX_WAIT = 120_000 // 2 minutes fallback

        const iv = setInterval(() => {
          if (token.value) {
            clearInterval(iv)
            resolve(token.value)
          } else if (error.value === 'error') {
            clearInterval(iv)
            reject(new Error('reCAPTCHA error'))
          } else if (Date.now() - startedAt > MAX_WAIT) {
            clearInterval(iv)
            // DO NOT reset here — let the user continue if the widget is still active.
            reject(new Error('reCAPTCHA took too long'))
          }
        }, 100)
      } catch (e) {
        reject(e as Error)
      }
    })
  }

  function destroy() {
    // v2 API doesn’t expose a destroy, but we can clear the container
    if (containerEl.value) containerEl.value.innerHTML = ''
    widgetId.value = null
    token.value = null
    ready.value = false
  }

  onMounted(render)
  onBeforeUnmount(destroy)

  return {
    containerEl,
    token,
    ready,
    error,
    render,
    reset,
    execute,
    destroy,
  }
}
